import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import BeatLoader from "react-spinners/BeatLoader";
import {
  Button,
  CustomInput,
  Description,
  SubTitle,
  UnorderedList,
} from "../../Components/CustomComponents";
import customStyles from "../../Components/CustomComponents/index.module.scss";
import { 
  useChangePasswordMutation,
  useChangePasswordWithOTPMutation 
} from "../../Data/services/settingApi";
import { getToken } from "../../Data/services/localStorageService";
import Toast from "../../Hooks/useToast";
import styles from "./styles/ChangePassword.module.scss";

export default function ChangePassword() {
  const [showOTPField, setShowOTPField] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm();

  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [changePasswordWithOTP, { isLoading: isOTPLoading }] = useChangePasswordWithOTPMutation();

  const handleDataSubmission = async (data) => {
    try {
      const response = await changePassword(data);
      
      if (response.error && !response.data) {
        Toast(response.error.data.message, "error");
      }

      if (!response.error && response.data.code === 200) {
        Toast(response?.data?.message || "OTP sent for change password", "success");
        setShowOTPField(true);
        setNewPassword(data.new_password);
        reset(); // Clear all form fields
      }
    } catch (error) {
      Toast(error?.response?.data?.message || "Failed to send OTP", "error");
    }
  };

  const handleOTPSubmission = async (data) => {
    try {
      const token = getToken();
      const response = await changePasswordWithOTP({
        otp: data.otp,
        new_password: newPassword,
        token: token,
      });

      if (response.error && !response.data) {
        Toast(response.error.data.message, "error");
      }

      if (!response.error && response.data.code === 200) {
        Toast(response?.data?.message || "Password changed successfully!", "success");
        setShowOTPField(false);
        setNewPassword("");
        reset();
      }
    } catch (error) {
      Toast(error?.response?.data?.message || "Failed to change password", "error");
    }
  };

  const handleBackToPasswordForm = () => {
    setShowOTPField(false);
    setNewPassword("");
    reset();
  };

  // Custom Password Input Component
  const PasswordInput = ({ field, placeholder, showPassword, setShowPassword }) => (
    <div className={styles.passwordInputContainer}>
      <Form.Control
        name={field.name}
        value={field.value || ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        className={`${customStyles.Input} mt-1 mb-2 text-black`}
      />
      <button
        type="button"
        className={styles.eyeButton}
        onClick={() => setShowPassword(!showPassword)}
      >
        {showPassword ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );

  return (
    <div className={styles.ChangePassword}>
      {errors
        ? errors.current_password?.message
          ? Toast(errors.current_password?.message, "error")
          : errors.new_password?.message
          ? Toast(errors.new_password?.message, "error")
          : errors.confirm_password?.message
          ? Toast(errors.confirm_password?.message, "error")
          : errors.otp?.message
          ? Toast(errors.otp?.message, "error")
          : null
        : null}
      
      <SubTitle text="Change Password" classes="" />
      <Description 
        text={showOTPField ? "Enter the OTP sent to your email to complete password change" : "Enter your current password and set a new password"} 
        classes="fs-8 mb-4" 
      />

      {!showOTPField ? (
        <Form onSubmit={handleSubmit(handleDataSubmission)}>
          <div className={styles.form}>
            <Description text="Current Password" classes="mb-1" />
            <Controller
              control={control}
              name="current_password"
              rules={{ required: "Current Password is required." }}
              render={({ field }) => (
                <PasswordInput
                  field={field}
                  placeholder="Current Password"
                  showPassword={showCurrentPassword}
                  setShowPassword={setShowCurrentPassword}
                />
              )}
            />
            <Description text="Set New Password" classes="mt-3 mb-1" />
            <Controller
              control={control}
              name="new_password"
              rules={{ required: "New Password is required." }}
              render={({ field }) => (
                <PasswordInput
                  field={field}
                  placeholder="New Password"
                  showPassword={showNewPassword}
                  setShowPassword={setShowNewPassword}
                />
              )}
            />
            <Controller
              control={control}
              name="confirm_password"
              rules={{ required: "Confirm Password is required." }}
              render={({ field }) => (
                <PasswordInput
                  field={field}
                  placeholder="Confirm Password"
                  showPassword={showConfirmPassword}
                  setShowPassword={setShowConfirmPassword}
                />
              )}
            />
            <UnorderedList
              lists={[
                "At least 12 characters long but 14 or more is better.",
                "A combination of uppercase letters, lowercase letters, numbers, and symbols.",
              ]}
              classes="fs-6 mt-4 mb-4"
            />

            <div className={styles.saveButton}>
              <Button
                text={isLoading ? <BeatLoader size={10} /> : "Send OTP"}
                classes={`fs-6 mb-2 mt-auto mt-3 mb-2 ${
                  isLoading ? "bg-dark-subtle" : ""
                }`}
                disabled={isLoading}
                type="submit"
              />
            </div>
          </div>
        </Form>
      ) : (
        <Form onSubmit={handleSubmit(handleOTPSubmission)}>
          <div className={styles.form}>
            <Description text="Enter OTP" classes="mb-1" />
            <Controller
              control={control}
              name="otp"
              rules={{ 
                required: "OTP is required.",
                pattern: {
                  value: /^\d{6}$/,
                  message: "OTP must be 6 digits"
                }
              }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <Form.Control
                  placeholder="Enter 6-digit OTP"
                  type="text"
                  maxLength={6}
                  value={value || ""}
                  onChange={(e) => {
                    // Only allow digits and limit to 6 characters
                    const inputValue = e.target.value.replace(/\D/g, '').slice(0, 6);
                    onChange(inputValue);
                  }}
                  onBlur={onBlur}
                  className={`${customStyles.Input} mt-1 mb-2 text-black`}
                />
              )}
            />

            <div className={styles.saveButton}>
              <div className={styles.buttonGroup}>
                <Button
                  text="Back"
                  classes="fs-6 mb-2 mt-auto mt-3 mb-2 me-2"
                  type="button"
                  onClick={handleBackToPasswordForm}
                  disabled={isOTPLoading}
                />
                <Button
                  text={isOTPLoading ? <BeatLoader size={10} /> : "Change Password"}
                  classes={`fs-6 mb-2 mt-auto mt-3 mb-2 ${
                    isOTPLoading ? "bg-dark-subtle" : ""
                  }`}
                  disabled={isOTPLoading}
                  type="submit"
                />
              </div>
            </div>
          </div>
        </Form>
      )}
    </div>
  );
}
